import { RequiredRule } from "./RequiredRule";
import { ValidEmailRule } from "./ValidEmailRule";
import { IsNumberRule } from "./IsNumberRule";
import { MaxLengthRule } from "./MaxLengthRule";
import { IsGreaterThanRule } from "./IsGreaterThanRule";
import { RegExRule } from "./RegExRule";
import { CustomRule } from "./CustomRule";
import { IsEqualToRule } from "./IsEqualToRule";

export class Validator
{
    private inputs: Record<string, IInputEntry> = {} as Record<string, IInputEntry>;
    private validationOptions: IValidationOptions;

    constructor(validationOptions: IValidationOptions)
    {
        this.validationOptions = validationOptions;
    }

    public get isValid(): boolean
    {
        return Object.values<IInputEntry>(this.inputs).every(i => i.isValid);
    }

    public addInput(id: string, getValue: () => string, setError: (error: string) => void): ValidationRuleBuilder
    {
        const input: IInputEntry = { id, isValid: true, getValue, setError, rules: [] };
        this.inputs[id] = input;
        return new ValidationRuleBuilder(input, this.validationOptions);
    }

    public removeInput(id: string): Validator
    {
        delete this.inputs[id];
        return this;
    }

    public validate(id: string): boolean
    {
        const input = this.inputs[id];
        return this.validateInput(input);
    }

    private validateInput(input: IInputEntry): boolean
    {
        if (!input) return true;
        const value = input.getValue();

        for (const rule of input.rules)
        {
            const error = rule.validate(value);
            if (error)
            {
                input.setError(error);
                input.isValid = false;
                return false;
            }
        }

        input.setError('');
        input.isValid = true;
        return true;
    }

    public validateAll(): boolean
    {
        let allValid = true;
        for (const inputId of Object.keys(this.inputs) as Array<string>)
        {
            const isValid = this.validate(inputId);
            if (!isValid)
            {
                allValid = false;
            }
        }
        return allValid;
    }
}

export class ValidationRuleBuilder
{
    private input: IInputEntry;
    private validationOptions: IValidationOptions;

    constructor(input: IInputEntry, validationOptions: IValidationOptions)
    {
        this.input = input;
        this.validationOptions = validationOptions;
    }

    public isRequired(options: IRuleOptions = {}): ValidationRuleBuilder
    {
        this.input.rules.push(new RequiredRule(this.validationOptions, options));
        return this;
    }

    public isValidEmail(options: IRuleOptions = {}): ValidationRuleBuilder
    {
        this.input.rules.push(new ValidEmailRule(this.validationOptions, options));
        return this;
    }

    public isNumber(options: IRuleOptions = {}): ValidationRuleBuilder
    {
        this.input.rules.push(new IsNumberRule(this.validationOptions, options));
        return this;
    }

    public hasMaxLength(maxLength: number, options: IRuleOptions = {}): ValidationRuleBuilder
    {
        this.input.rules.push(new MaxLengthRule(maxLength, this.validationOptions, options));
        return this;
    }

    public isEqualTo(getValueToCompare: () => string, options: IRuleOptions = {}): ValidationRuleBuilder
    {
        this.input.rules.push(new IsEqualToRule(getValueToCompare, this.validationOptions, options));
        return this;
    }

    public isGreaterThan(value: number, options: IRuleOptions = {}): ValidationRuleBuilder
    {
        this.input.rules.push(new IsGreaterThanRule(value, this.validationOptions, options));
        return this;
    }

    public matchesRegEx(regEx: RegExp, options: IRuleOptions = {}): ValidationRuleBuilder
    {
        this.input.rules.push(new RegExRule(regEx, this.validationOptions, options));
        return this;
    }

    public customRule(validate: (value: string) => boolean, options: IRuleOptions = {}): ValidationRuleBuilder
    {
        this.input.rules.push(new CustomRule(validate, this.validationOptions, options));
        return this;
    }
}

export interface IInputEntry
{
    id: string;
    isValid: boolean;
    getValue: () => string;
    setError: (error: string) => void;
    rules: IValidationRule[];
}

export interface IValidationRule
{
    rule: RuleName;
    validate(value: string): string;
}

export interface IValidationOptions
{
    invalidFieldLabel: string;
    isRequiredLabel?: string;
    mustBeNumberLabel?: string;
    mustBeEqualToLabel?: string;
    mustBeGreaterThanLabel?: string;
    mustBeMaxLengthLabel?: string;
    mustBeValidEmailLabel?: string;
}

export interface IRuleOptions
{
    errorLabel?: string;
}

export type RuleName = 'Required' | 'IsNumber' | 'IsEqualTo' | 'IsGreaterThan' | 'MaxLength' | 'RegEx' | 'ValidEmail' | 'Custom';
